import React, {useImperativeHandle, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import {useChartMouse, useComponentProps} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import {CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import ChartTooltip from 'components/molecules/Tooltips/ChartTooltip/ChartTooltip';
import ActionArea from 'components/molecules/Charts/ActionArea/ActionArea';
import StyledAreaChart from 'components/organisms/Charts/AreaChart/AreaChart.styles';

const AreaChart = React.forwardRef((props, ref) => {
  const {
    data,
    dataKey,
    hoveredId,
    areas,
    showTooltip,
    showCursor,
    showGrid,
    isLoading,
    hideAxis,
    reverse,
    XAxisProps,
    YAxisProps,
    onClick,
    TooltipComponent,
    TooltipProps,
    GridProps,
    ResponsiveContainerProps,
    ...innerProps
  } = useComponentProps({
    dataKey: 'name',
    showTooltip: true,
    showCursor: false,
    areas: [],
    hideAxis: false,
    reverse: false,
    layout: 'horizontal',
    TooltipComponent: ChartTooltip,
    ...props
  }, 'AreaChart', {
    static: ['isLoading'],
    children: ['tooltip', 'area']
  });

  const innerRef = useRef(null);
  const containerRef = useRef(null);

  const AreaChart = useMemo(() => ({
    refs: {
      ref: innerRef,
      containerRef
    }
  }), []);

  useImperativeHandle(ref, () => AreaChart);

  const chartMouse = useChartMouse();

  const areasMemo = useMemo(() => {
    const sorted = areas
      .sort((a, b) => {
        if (utils.isDefined(a.position) && utils.isDefined(b.position)) {
          return a.position - b.position;
        } else {
          return a.label.localeCompare(b.label);
        }
      });

    return reverse ? sorted.reverse() : areas;
  }, [areas, reverse]);

  const sortedData = useMemo(() => {
    if (data) {
      return data
        .sort((a, b) => {
          if (utils.isDefined(a.position) && utils.isDefined(b.position)) {
            return a.position - b.position;
          } else {
            return (a.label ?? a.name).localeCompare(b.label ?? b.name);
          }
        })
    }
  }, [data]);

  const horizontal = innerProps.layout !== 'vertical';
  const type = innerProps.type ?? (sortedData?.some((v) => !utils.isNumber(v[dataKey])) ? 'category' : 'number');

  const renderAreas = () => {
    if (sortedData) {
      return areasMemo
        .map((area, areaIdx) => {
          const color = area.color ?? innerProps.theme.property('palette.primary.main');
          const hovering = utils.isDefined(hoveredId) || chartMouse.isHovering();
          const hovered = utils.isDefined(hoveredId) ? (hoveredId === area.id) :
            chartMouse.isDetailHovering(area.dataKey)

          return <ActionArea key={areaIdx}
                             className={`AreaChart-area ${hovering ? (hovered ? 'hover' : 'notHover') : ''}`}
                             name={area.name}
                             dataKey={area.dataKey}
                             hovered={hovered}
                             hoverDot={true}
                             dot={true}
                             type="monotone"
                             stroke={color}
                             fill={color}
                             strokeWidth={2}
                             onClick={onClick}
                             onMouseMove={chartMouse.handleDetailMouseMove(area.dataKey)}
                             {...area.AreaProps} />
        });
    }
  }
  innerProps.onMouseOut = innerProps.onMouseOut ?? chartMouse.handleChartMouseOut;
  innerProps.margin = innerProps.margin ?? {
    top: 8, right: 8, bottom: 4, left: 0
  };

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <ResponsiveContainer ref={AreaChart.refs.containerRef} {...ResponsiveContainerProps}>
    <StyledAreaChart ref={AreaChart.refs.ref} {...innerProps} data={sortedData}>
      {showGrid && <CartesianGrid strokeDasharray="3 3" {...GridProps}/>}
      {sortedData?.length > 0 ? <XAxis hide={hideAxis}
                                       dataKey={horizontal ? dataKey : null}
                                       type={horizontal ? type : 'number'}
                                       tickSize={16}
                                       tickLine={false}
                                       allowDecimals={false}
                                       {...XAxisProps}/> : null}
      {sortedData?.length > 0 ? <YAxis hide={hideAxis}
                                       dataKey={!horizontal ? dataKey : null}
                                       type={!horizontal ? type : 'number'}
                                       tickSize={12}
                                       tickLine={false}
                                       allowDecimals={false}
                                       {...YAxisProps}/> : null}
      {showTooltip && <Tooltip className="AreaChart-tooltip"
                               cursor={showCursor}
                               content={<TooltipComponent wrapper={AreaChart.refs.containerRef.current?.current}
                                                          activateByPayload={true}
                                                          activePayload={chartMouse.hoverPayLoadKey}
                                                          activeIndex={chartMouse.activeIndex}
                                                          {...TooltipProps}/>} />}
      {renderAreas()}
    </StyledAreaChart>
  </ResponsiveContainer>
});

AreaChart.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  dataKey: PropTypes.string,
  hoveredId: PropTypes.any,
  bars: PropTypes.array,
  showTooltip: PropTypes.bool,
  showCursor: PropTypes.bool,
  showGrid: PropTypes.bool,
  isLoading: PropTypes.bool,
  TooltipComponent: PropTypes.any,
  XAxisProps: PropTypes.object,
  YAxisProps: PropTypes.object,
  onClick: PropTypes.func,
  TooltipProps: PropTypes.object,
  GridProps: PropTypes.object,
  ResponsiveContainerProps: PropTypes.object
};

export default AreaChart;
